import {
    Table,
    TableBorderRadius,
    TableBorderStyle,
    TableColumn,
    TableHeaderTheme,
    TableRow,
    TableRowTheme,
} from '@apply/components';
import { FileTypeOption, LoanFile } from './constants/loanFile';
import { FileStatus } from './components/FileStatus';
import { DownloadFileButton } from './components/DownloadFileButton';
import { RemoveFileButton } from './components/RemoveFileButton';
import { AddFiles } from './components/AddFiles/AddFiles';

interface LoanFilesTableProps {
    id: string;
    files: LoanFile[];
    /** The types files can have (e.g. LoanFileTypeOptions or LoanPropertyFileTypeOptions). */
    fileTypeOptions: FileTypeOption[];
    /** Called with the whole updated list whenever a file is added, removed or restored. */
    onChange: (files: LoanFile[]) => void;
}

/**
 * A loan's (or property's) files: each one's type, name and status, with buttons to download it and remove it, and a
 * row to add new ones. A new version of LoanFilesGrid. Changes aren't saved here: new files are uploaded, and removed
 * ones deleted, when the loan is saved.
 */
export const LoanFilesTable = ({ id, files, fileTypeOptions, onChange }: LoanFilesTableProps) => {
    const getFileTypeName = (fileTypeId: number) => fileTypeOptions.find((option) => option.id === fileTypeId)?.name;

    // A new file is just dropped; a saved one is marked, to be deleted when the loan is saved
    const removeFile = (file: LoanFile) =>
        onChange(
            file.id === 0 ? files.filter((f) => f !== file) : files.map((f) => (f === file ? { ...f, isDeleted: true } : f))
        );

    const restoreFile = (file: LoanFile) => onChange(files.map((f) => (f === file ? { ...f, isDeleted: false } : f)));

    const rows: TableRow[] = files.map((file, index) => ({
        fileType: getFileTypeName(file.fileTypeId),
        fileName: file.fileName,
        status: <FileStatus file={file} />,
        download: (
            <DownloadFileButton
                id={`${id}_download_${index}`}
                url={file.url}
            />
        ),
        remove: (
            <RemoveFileButton
                id={`${id}_remove_${index}`}
                file={file}
                onRemove={() => removeFile(file)}
                onRestore={() => restoreFile(file)}
            />
        ),
    }));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Table
                data={rows}
                headerTheme={TableHeaderTheme.Gray}
                rowTheme={TableRowTheme.White}
                borderStyle={TableBorderStyle.Solid}
                borderRadius={TableBorderRadius.Small}
                width='100%'
                emptyTableText='No Files found'
            >
                <TableColumn
                    columnKey='fileType'
                    header='Type'
                    width='20%'
                />
                <TableColumn
                    columnKey='fileName'
                    header='Name'
                    width='40%'
                />
                <TableColumn
                    columnKey='status'
                    header='Status'
                    width='20%'
                />
                <TableColumn
                    columnKey='download'
                    width='10%'
                    actionColumn={true}
                    center={true}
                />
                <TableColumn
                    columnKey='remove'
                    width='10%'
                    actionColumn={true}
                    center={true}
                />
            </Table>
            <AddFiles
                id={`${id}_add`}
                fileTypeOptions={fileTypeOptions}
                onAdd={(newFiles) => onChange([...files, ...newFiles])}
            />
        </div>
    );
};
